import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Trash2, RefreshCw, LogOut, Mail, Phone, Building2, Clock, MessageSquare, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ admin, onLogout }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const token = localStorage.getItem("ozo_admin_token");

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts((prev) => prev.filter((c) => c.id !== id));
      if (selectedContact?.id === id) setSelectedContact(null);
    } catch (err) {
      alert("Failed to delete. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("ozo_admin_token");
    onLogout();
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EC]">
      {/* Header */}
      <header className="bg-white border-b border-[#0C3A72]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-to-site-header" className="text-[#4A627E] hover:text-[#0077D1] transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div>
              <h1 className="text-lg font-semibold text-[#0C3A72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Admin Dashboard
              </h1>
              <p className="text-xs text-[#4A627E]">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchContacts}
              data-testid="admin-refresh-btn"
              className="p-2 text-[#4A627E] hover:text-[#0077D1] hover:bg-[#0077D1]/10 transition-all"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[#0C3A72] border border-[#0C3A72]/15 hover:bg-[#0C3A72] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#0C3A72]/10 p-5" data-testid="admin-stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0077D1]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0077D1]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0C3A72]">{contacts.length}</p>
                <p className="text-xs text-[#4A627E]">Total Submissions</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#0C3A72]/10 p-5" data-testid="admin-stat-today">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0C3A72]">
                  {contacts.filter((c) => {
                    const d = new Date(c.created_at);
                    const now = new Date();
                    return d.toDateString() === now.toDateString();
                  }).length}
                </p>
                <p className="text-xs text-[#4A627E]">Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#0C3A72]/10 p-5" data-testid="admin-stat-week">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0C3A72]">
                  {contacts.filter((c) => {
                    const d = new Date(c.created_at);
                    const week = new Date();
                    week.setDate(week.getDate() - 7);
                    return d >= week;
                  }).length}
                </p>
                <p className="text-xs text-[#4A627E]">This Week</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Contact List */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-[#0C3A72]/10">
              <div className="px-5 py-4 border-b border-[#0C3A72]/10">
                <h2 className="text-sm uppercase tracking-[0.15em] font-medium text-[#0C3A72]">
                  Contact Submissions
                </h2>
              </div>

              {loading ? (
                <div className="p-8 text-center text-[#4A627E] text-sm">Loading submissions...</div>
              ) : contacts.length === 0 ? (
                <div className="p-8 text-center text-[#4A627E] text-sm" data-testid="admin-no-contacts">
                  No contact submissions yet.
                </div>
              ) : (
                <div className="divide-y divide-[#0C3A72]/5" data-testid="admin-contact-list">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      data-testid={`admin-contact-row-${contact.id}`}
                      onClick={() => setSelectedContact(contact)}
                      className={`px-5 py-4 cursor-pointer hover:bg-[#F7F4EC]/50 transition-colors ${
                        selectedContact?.id === contact.id ? "bg-[#0077D1]/5 border-l-2 border-l-[#0077D1]" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-[#0C3A72] truncate">{contact.name}</p>
                            {contact.company && (
                              <span className="text-xs text-[#4A627E] bg-[#F7F4EC] px-2 py-0.5 truncate">{contact.company}</span>
                            )}
                          </div>
                          <p className="text-xs text-[#4A627E] truncate">{contact.email}</p>
                          <p className="text-xs text-[#4A627E]/60 mt-1 line-clamp-1">{contact.message}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-[#4A627E]/50 whitespace-nowrap">{formatDate(contact.created_at)}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteContact(contact.id); }}
                            data-testid={`admin-delete-${contact.id}`}
                            disabled={deleting === contact.id}
                            className="p-1.5 text-[#4A627E]/40 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            {selectedContact ? (
              <div className="bg-white border border-[#0C3A72]/10 p-6 sticky top-24" data-testid="admin-contact-detail">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-sm uppercase tracking-[0.15em] font-medium text-[#0077D1]">Detail</h3>
                  <button
                    onClick={() => deleteContact(selectedContact.id)}
                    data-testid="admin-detail-delete"
                    className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:bg-red-50 px-2 py-1 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>

                <div className="space-y-5">
                  <DetailRow icon={Mail} label="Name" value={selectedContact.name} />
                  {selectedContact.company && <DetailRow icon={Building2} label="Company" value={selectedContact.company} />}
                  <DetailRow icon={Mail} label="Email" value={selectedContact.email} href={`mailto:${selectedContact.email}`} />
                  {selectedContact.phone && <DetailRow icon={Phone} label="Phone" value={selectedContact.phone} href={`tel:${selectedContact.phone}`} />}
                  <DetailRow icon={Clock} label="Submitted" value={formatDate(selectedContact.created_at)} />

                  <div className="pt-4 border-t border-[#0C3A72]/10">
                    <p className="text-xs text-[#4A627E] mb-2 uppercase tracking-wider font-medium">Message</p>
                    <p className="text-sm text-[#0C3A72] leading-relaxed whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>

                  <a
                    href={`mailto:${selectedContact.email}?subject=Re: OZO AeroCare Inquiry&body=Dear ${selectedContact.name},%0A%0AThank you for contacting OZO AeroCare.%0A%0A`}
                    data-testid="admin-reply-btn"
                    className="block w-full text-center bg-[#0077D1] text-white py-2.5 text-sm uppercase tracking-[0.1em] font-medium hover:bg-[#005BA1] transition-colors mt-4"
                  >
                    Reply via Email
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-[#0C3A72]/10 p-8 text-center" data-testid="admin-no-selection">
                <MessageSquare className="w-8 h-8 text-[#0C3A72]/20 mx-auto mb-3" />
                <p className="text-sm text-[#4A627E]">Select a submission to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, href }) {
  const content = href ? (
    <a href={href} className="text-[#0077D1] hover:underline">{value}</a>
  ) : (
    <span className="text-[#0C3A72]">{value}</span>
  );
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#4A627E]/50 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-[#4A627E]/60 mb-0.5">{label}</p>
        <p className="text-sm font-medium">{content}</p>
      </div>
    </div>
  );
}
